import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterBarProps {
  groups: FilterGroup[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
}

export default function FilterBar({
  groups,
  values,
  onChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const activeCount = groups.filter(
    (group) => values[group.key] && values[group.key] !== "all",
  ).length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-100">
      <div className="flex items-center justify-between gap-3 p-4 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line" />
          筛选条件
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-bold text-background-50">
              {activeCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1.5 text-sm font-medium text-foreground-500 cursor-pointer"
        >
          <i className="ri-refresh-line" />
          重置
        </button>
      </div>

      <div className={`${open ? "block" : "hidden"} border-t border-background-200 p-4 lg:block lg:border-t-0 lg:p-5`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
          {groups.map((group) => (
            <div key={group.key} className="lg:min-w-0">
              <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-foreground-500">
                <span className="h-1 w-1 rounded-full bg-primary-500" />
                {group.label}
              </div>
              <div className="flex flex-wrap gap-2">
                {group.options.map((option) => {
                  const isActive = (values[group.key] || "all") === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onChange(group.key, option.value)}
                      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        isActive
                          ? "border-primary-500 bg-primary-500/15 text-primary-400"
                          : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-500/40 hover:text-foreground-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={onReset}
            className="hidden items-center gap-1.5 text-sm font-medium text-foreground-500 transition-colors hover:text-primary-400 lg:ml-auto lg:flex whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}